/**
 * Base error class for the service layer.
 * All services throw ServiceError on failure.
 *
 * IMPORTANT: `message` is the engineer-facing string (often the raw
 * Postgres / Supabase / SDK message). It belongs in Sentry and DevTools,
 * NOT in the user-facing toast. UI catch sites should resolve a friendly
 * message via `errorMessage(err, fallback)` (see lib/utils/observability.ts)
 * which consults `userMessageKey` first before falling back to the i18n
 * fallback string the caller provides.
 *
 * Concretely: this is what stops `duplicate key value violates unique
 * constraint "identity_documents_profile_id_document_type..."` from ever
 * reaching the user as a toast. The same SQLSTATE map is also used to
 * convert Postgres errors to friendly strings without each catch site
 * having to rewrite the same switch.
 */
export class ServiceError extends Error {
  readonly code: string
  readonly statusCode: number
  readonly details?: Record<string, unknown>
  /**
   * i18n key (e.g. `errors.unique_violation`). UI catch sites resolve this
   * via i18next to get a user-facing translated string. Null when the
   * service-layer error doesn't have a known friendly mapping — caller
   * supplies its own fallback in that case.
   */
  readonly userMessageKey: string | null

  constructor(params: {
    message: string
    code: string
    statusCode?: number
    details?: Record<string, unknown>
    userMessageKey?: string | null
    cause?: unknown
  }) {
    super(params.message, { cause: params.cause })
    this.name = 'ServiceError'
    this.code = params.code
    this.statusCode = params.statusCode ?? 500
    this.details = params.details
    this.userMessageKey = params.userMessageKey ?? null
  }

  static unauthorized(message = 'Unauthorized'): ServiceError {
    return new ServiceError({
      message,
      code: 'UNAUTHORIZED',
      statusCode: 401,
      userMessageKey: 'errors.unauthorized',
    })
  }

  static forbidden(message = 'Forbidden'): ServiceError {
    return new ServiceError({
      message,
      code: 'FORBIDDEN',
      statusCode: 403,
      userMessageKey: 'errors.forbidden',
    })
  }

  static badRequest(message: string): ServiceError {
    return new ServiceError({
      message,
      code: 'BAD_REQUEST',
      statusCode: 400,
      userMessageKey: 'errors.bad_request',
    })
  }

  static notFound(resource: string): ServiceError {
    return new ServiceError({
      message: `${resource} not found`,
      code: 'NOT_FOUND',
      statusCode: 404,
      userMessageKey: 'errors.not_found',
    })
  }

  static validation(message: string, details?: Record<string, unknown>): ServiceError {
    return new ServiceError({
      message,
      code: 'VALIDATION_ERROR',
      statusCode: 422,
      details,
      userMessageKey: 'errors.validation',
    })
  }

  static rateLimited(): ServiceError {
    return new ServiceError({
      message: 'Too many requests',
      code: 'RATE_LIMITED',
      statusCode: 429,
      userMessageKey: 'errors.rate_limited',
    })
  }

  /**
   * Map of Postgres SQLSTATE codes (and a few Supabase-specific ones) to
   * user-facing i18n keys. Anything not in the map keeps `userMessageKey`
   * null and the catch site falls back to its own provided string.
   *
   * SQLSTATE reference: https://www.postgresql.org/docs/current/errcodes-appendix.html
   * PGRST codes: https://postgrest.org/en/stable/errors.html
   */
  private static SQLSTATE_TO_KEY: Record<string, string> = {
    '23505': 'errors.unique_violation', // duplicate key
    '23503': 'errors.fk_violation', // FK constraint
    '23514': 'errors.check_violation', // CHECK constraint
    '23502': 'errors.not_null_violation', // NOT NULL violation
    '23P01': 'checkout.dates_unavailable', // exclusion constraint (rental overlap)
    '42501': 'errors.permission_denied', // RLS / privilege
    '42P01': 'errors.not_found', // undefined table
    PGRST116: 'errors.not_found', // no rows from .single()
    /*
     * PGRST301 es "JWT expired": la sesion se vencio, no falta un permiso.
     *
     * Decirle "No tenes permisos para esta accion" a alguien cuya sesion
     * caduco lo manda a buscar un problema que no existe -- y la salida real,
     * volver a entrar, no se le ocurre porque nadie se la nombra.
     * `errors.unauthorized` ya dice justo eso: "Tu sesion expiro. Inicia sesion
     * de nuevo." Es la quinta causa distinta de esa misma frase que aparece hoy,
     * y la unica que se arregla cambiando a que texto apunta.
     */
    PGRST301: 'errors.unauthorized', // JWT expired
    '57014': 'errors.timeout', // statement timeout
    '53300': 'errors.too_many_connections',
  }

  static fromSupabaseError(error: {
    message: string
    code?: string
    error_code?: string
    status?: number
    statusCode?: number | string
    name?: string
    context?: { status?: number }
    hint?: string | null
  }): ServiceError {
    /*
     * supabase-js represents a non-2xx Edge Function response as a
     * FunctionsHttpError whose HTTP status lives on `context.status`, not on
     * the top-level object. The old mapper discarded that context and turned a
     * user/input 4xx into a synthetic 500. Every caller then reported a
     * recoverable response to Sentry as a production failure (TAGO-MOBILE-4N)
     * and the UI lost the distinction between "fix the request" and "the
     * function is down".
     *
     * Preserve the transport boundary here, once, so every service gets the
     * same behavior instead of each catch site reverse-engineering the SDK
     * error shape.
     */
    const functionError =
      error.name === 'FunctionsHttpError' || error.name === 'VerificationHttpError'
    if (functionError) {
      const status = errorStatus(error) ?? 500
      const isVerification = error.name === 'VerificationHttpError'
      const upstreamCode = errorCode(error)
      return new ServiceError({
        message: error.message,
        code: isVerification ? 'VERIFICATION_HTTP_ERROR' : 'FUNCTIONS_HTTP_ERROR',
        statusCode: status,
        userMessageKey:
          status === 401 || status === 403
            ? 'errors.unauthorized'
            : status >= 400 && status < 500
              ? 'errors.validation'
              : null,
        details: {
          upstreamStatus: status,
          ...(upstreamCode ? { upstreamCode } : {}),
        },
        cause: error,
      })
    }

    /*
     * "No tenés permisos" no es una explicación, es un callejón.
     *
     * `private.assert_rental_insert_is_a_request` empezó a rechazar reservas de
     * quien todavía no puede pagar --antes se creaba la fila igual, se apartaba
     * el carro y media hora después moría sola-- y lo hace con SQLSTATE 42501,
     * que este mapa traduce a "No tenés permisos para esta acción."
     *
     * Ese es exactamente el texto que el dueño señaló como inaceptable, y con
     * razón: describe el resultado y esconde la causa. Lo que falta no es un
     * permiso, son trámites, y el producto sabe cuáles.
     *
     * El HINT del trigger trae la lista legible por máquina. Se lee acá y se le
     * da su propia copia, igual que ya tienen el choque de fechas y la caída del
     * proveedor de OTP: una condición conocida merece una frase propia, no la
     * genérica de su código de error.
     */
    const eligibilityBlockers = rentalEligibilityBlockersFrom(error.hint)
    if (eligibilityBlockers) {
      return new ServiceError({
        message: error.message,
        code: 'BOOKING_NOT_ELIGIBLE',
        statusCode: 422,
        // Los pasos concretos los pinta la pantalla desde
        // `lib/verification/steps.ts`; acá solo viaja la lista, sin un cuarto
        // juego de etiquetas para los mismos seis trámites.
        details: { blockers: eligibilityBlockers },
        userMessageKey: eligibilityBlockers.includes('phone')
          ? 'verification.blocked_toast_phone'
          : 'verification.blocked_toast_steps',
        cause: error,
      })
    }

    // A listing can be retired between opening checkout and creating the
    // reservation. That race is neither a payment failure nor a permission
    // problem, so give the checkout a stable, non-Sentry outcome.
    if (error.hint === RENTAL_VEHICLE_NOT_RESERVABLE_HINT) {
      return new ServiceError({
        message: error.message,
        code: 'BOOKING_VEHICLE_UNAVAILABLE',
        statusCode: 409,
        userMessageKey: 'checkout.vehicle_no_longer_available',
        cause: error,
      })
    }

    if (error.hint === 'rental_host_response_minimum_lead') {
      return new ServiceError({
        message: error.message,
        code: 'BOOKING_TOO_SOON',
        statusCode: 422,
        userMessageKey: 'rental_terms_flow.minimum_lead',
        cause: error,
      })
    }

    if (error.hint === 'host_decision_expired' || error.hint === 'host_decision_already_resolved') {
      return new ServiceError({
        message: error.message,
        code: 'HOST_DECISION_UNAVAILABLE',
        statusCode: 409,
        userMessageKey: 'host_approval.expired',
        cause: error,
      })
    }

    /*
     * LA EVIDENCIA DEL TRASPASO, DICHA COMO FRASE.
     *
     * `private.validate_rental_status_transition` rechaza avanzar la renta
     * cuando la inspección no trae las quince fotos del carro y las dos firmas.
     * Sin esto, ese rechazo llegaba al teléfono como un SQLSTATE crudo — y
     * `errorMessage()` no muestra `err.message` en producción, así que la
     * persona veía la frase genérica de un código de error mientras estaba
     * parada junto al carro sin entender qué le falta.
     *
     * Mismo mecanismo que la elegibilidad de pago: el motivo legible por
     * máquina viaja en el HINT y acá se le da su propia copia.
     */
    if (error.hint === 'tago.inspection_evidence_incomplete') {
      return new ServiceError({
        message: error.message,
        code: 'INSPECTION_EVIDENCE_INCOMPLETE',
        statusCode: 422,
        userMessageKey: 'inspection.evidence_incomplete',
        cause: error,
      })
    }

    /*
     * Cada inspección que falta se explica en SU pantalla.
     *
     * Los dos casos compartían hint y por lo tanto frase: la de la devolución
     * ("la entrega va primero"). A quien estuviera en la pantalla de ENTREGA se
     * le explicaba el orden de la DEVOLUCIÓN — una frase que no aplica a lo que
     * tiene enfrente, justo donde menos margen hay para adivinar.
     */
    if (error.hint === 'tago.dropoff_inspection_missing') {
      return new ServiceError({
        message: error.message,
        code: 'INSPECTION_MISSING',
        statusCode: 409,
        userMessageKey: 'inspection.stage.checkin_first_body',
        cause: error,
      })
    }

    if (error.hint === 'tago.pickup_inspection_missing') {
      return new ServiceError({
        message: error.message,
        code: 'INSPECTION_MISSING',
        statusCode: 409,
        userMessageKey: 'inspection.pickup_missing_body',
        cause: error,
      })
    }

    if (isReservationDateConflictError(error)) {
      return new ServiceError({
        message: error.message,
        code: 'DATES_UNAVAILABLE',
        statusCode: error.status ?? 409,
        userMessageKey: 'checkout.dates_unavailable',
        cause: error,
      })
    }

    if (isRentalStartInThePastError(error)) {
      return new ServiceError({
        message: error.message,
        code: 'RENTAL_START_IN_THE_PAST',
        statusCode: 422,
        userMessageKey: 'checkout.start_in_the_past',
        cause: error,
      })
    }

    if (isVehicleDocumentExpiredBookingError(error)) {
      return new ServiceError({
        message: error.message,
        code: 'VEHICLE_DOCUMENT_EXPIRED',
        statusCode: 422,
        userMessageKey: 'checkout.vehicle_document_expired',
        cause: error,
      })
    }

    if (isDuplicateAccountRegistrationError(error)) {
      return new ServiceError({
        message: error.message,
        code: 'AUTH_ACCOUNT_EXISTS',
        statusCode: error.status ?? 409,
        userMessageKey: 'auth.errors.account_exists',
        cause: error,
      })
    }

    // Auth password validation keeps the recovery session valid. Showing the
    // reset screen's generic fallback here falsely tells the user the link
    // expired even though /verify succeeded and /user rejected only the input.
    const passwordValidationKeys: Record<string, string> = {
      same_password: 'auth.errors.password_unchanged',
      weak_password: 'auth.errors.password_weak',
    }
    const passwordValidationKey = passwordValidationKeys[error.code ?? '']
    if (passwordValidationKey) {
      return new ServiceError({
        message: error.message,
        code: error.code ?? 'PASSWORD_VALIDATION_ERROR',
        statusCode: error.status ?? 422,
        userMessageKey: passwordValidationKey,
        cause: error,
      })
    }

    if (isAuthSessionMissingError(error)) {
      return new ServiceError({
        message: error.message,
        code: 'AUTH_SESSION_MISSING',
        statusCode: error.status ?? 401,
        userMessageKey: 'auth.errors.reset_link_expired',
        cause: error,
      })
    }

    // Una caída del proveedor de códigos. Copia propia, para que nadie
    // confunda "nuestro envío está caído" con "escribí mal mi número".
    if (classifyPhoneOtpDeliveryError(error) === 'provider_rejected') {
      return new ServiceError({
        message: error.message,
        code: error.code ?? 'SMS_SEND_FAILED',
        statusCode: error.status ?? 422,
        userMessageKey: 'auth.verify_phone_sms_provider_body',
        cause: error,
      })
    }

    const code = error.code ?? 'SUPABASE_ERROR'
    return new ServiceError({
      // Keep the raw message in `message` for Sentry / DevTools — engineers
      // need the SQLSTATE / constraint name to debug. UI catch sites read
      // `userMessageKey` instead so the user never sees a SQLSTATE.
      message: error.message,
      code,
      statusCode: error.status ?? 500,
      userMessageKey: ServiceError.SQLSTATE_TO_KEY[code] ?? null,
      cause: error,
    })
  }
}

// ─── Por qué se rechazó el envío del código ─────────────────────────────────
//
// ESTO SE LEÍA DE LA PROSA, Y ESA ERA LA FALLA. La clasificación buscaba frases
// en el mensaje que devolvía el proveedor a través de GoTrue —`twilio.*20003`,
// "account … is not active", incluso la URL de documentación que Twilio pega al
// final— y el comentario que lo acompañaba dejó registrado lo peor de todo: una
// caída de proveedor se reconocía POR EL ENLACE. El día que ese enlace se
// recortara, la pantalla habría vuelto a "no pudimos enviar el código", que se
// lee como culpa de quien escribió el número.
//
// Ahora el servidor manda un código estable en `{ error, code }` y la
// clasificación lo lee. Reescribir un mensaje ya no puede cambiar lo que la app
// decide, que es la única propiedad que hacía falta y la que la prosa no podía
// dar.
//
// Las tres razones siguen siendo tres porque piden tres cosas distintas de la
// persona: esperar un rato, esperar más, o dejar de esperar y salir por correo.

export type PhoneOtpDeliveryReason =
  /** El servicio está saturado o en mantenimiento. El número está bien. */
  | 'service_rate_limited'
  /** Esta cuenta o este número pidieron demasiados códigos. Se recupera esperando. */
  | 'account_rate_limited'
  /** El proveedor falló o no hay saldo. Es nuestro, y esperar no lo arregla. */
  | 'provider_rejected'

/**
 * Los códigos que emiten send-phone-otp y verify-phone-otp.
 *
 * `OTP_SERVICE_UNAVAILABLE` es lo que el servidor responde cuando Verifik se
 * quedó sin saldo — deliberadamente sin decírselo al usuario, porque no hay
 * nada que pueda hacer con esa información y sí hay algo que podemos hacer
 * nosotros: la alerta crítica sale del lado del servidor.
 */
const OTP_CODE_REASONS: Record<string, PhoneOtpDeliveryReason> = {
  OTP_RECENTLY_SENT: 'account_rate_limited',
  OTP_RATE_LIMITED: 'account_rate_limited',
  OTP_TOO_MANY_ATTEMPTS: 'account_rate_limited',
  OTP_SERVICE_UNAVAILABLE: 'provider_rejected',
  OTP_SEND_FAILED: 'provider_rejected',
}

/** El número no puede recibir el código. Es entrada del usuario, no una caída. */
export function isInvalidPhoneNumberError(err: unknown): boolean {
  const code = errorCode(err)
  return code === 'PHONE_UNDELIVERABLE' || code === 'PHONE_INVALID'
}

/**
 * Cuál de las tres razones es, o null cuando el error es otra cosa
 * (un código mal tecleado, la red, un número inválido).
 *
 * El orden importa: un número inválido se descarta primero. Contarle a alguien
 * que "el servicio está saturado" cuando lo que pasa es que le falta un dígito
 * lo manda a esperar por algo que nunca se va a arreglar solo.
 */
export function classifyPhoneOtpDeliveryError(err: unknown): PhoneOtpDeliveryReason | null {
  if (isInvalidPhoneNumberError(err)) return null
  const code = errorCode(err)
  if (code && OTP_CODE_REASONS[code]) return OTP_CODE_REASONS[code]
  // Un 503 sin código es el servicio caído de todos modos — el límite de tasa
  // compartido falla-cerrado así, sin código.
  if (errorStatus(err) === 503) return 'service_rate_limited'
  return null
}

export function isPhoneOtpDeliveryError(err: unknown): boolean {
  return classifyPhoneOtpDeliveryError(err) !== null
}

/** Capacidad, no rotura. Esperado, se recupera esperando. */
export function isPhoneOtpRateLimitError(err: unknown): boolean {
  // Status-only 503 can inform retry copy inside the OTP screen, but does
  // not prove capacity in the shared Sentry classifier (payments also fail 503).
  const code = errorCode(err)
  return code !== null && OTP_CODE_REASONS[code] === 'account_rate_limited'
}

/** Nuestro proveedor está roto o sin saldo. Nunca esperado. Siempre a Sentry. */
export function isPhoneOtpProviderError(err: unknown): boolean {
  return classifyPhoneOtpDeliveryError(err) === 'provider_rejected'
}

/** El número ya pertenece a otra cuenta (vincular / cambiar número). */
export function isPhoneInUseError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as {
    userMessageKey?: string | null
    code?: string
    message?: string
    statusCode?: number
  }
  if (e.userMessageKey === 'auth.errors.phone_in_use') return true
  if (e.code === 'PHONE_IN_USE') return true
  if (e.statusCode === 409 && /phone/i.test(e.message ?? '')) return true
  return false
}

/**
 * Ese número pertenece a una cuenta que entra por correo.
 *
 * Su recuperación es distinta a todo lo demás: no es esperar ni corregir un
 * dígito, es usar la otra puerta. Confundirla con un fallo de envío deja a
 * alguien reintentando un camino que está cerrado a propósito.
 */
export function isPhoneAccountUsesEmailError(err: unknown): boolean {
  return errorCode(err) === 'OTP_USE_EMAIL_LOGIN'
}

/** No hay cuenta con ese número (inicio de sesión, no registro). */
export function isPhoneAccountNotFoundError(err: unknown): boolean {
  const code = errorCode(err)
  return code === 'PHONE_ACCOUNT_NOT_FOUND' || code === 'PHONE_USER_NOT_FOUND'
}

/**
 * Cuántos segundos dijo el servidor que hay que esperar.
 *
 * Sale de `Retry-After`, que el servicio ya tradujo a `details.retryAfterSeconds`.
 * Repetir ese número es la diferencia entre un contador en el que se puede
 * confiar y uno que miente dos veces: al llegar a cero y al volver a fallar.
 */
export function phoneOtpRetryAfterSeconds(err: unknown): number | null {
  const details = (err as { details?: { retryAfterSeconds?: unknown } }).details
  const seconds = details?.retryAfterSeconds
  if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
    return Math.min(seconds, 3600)
  }
  return null
}

function errorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null
  const item = err as {
    code?: unknown
    error_code?: unknown
    paymentCode?: unknown
    details?: { upstreamCode?: unknown; upstream_code?: unknown }
  }
  // `paymentCode` is the canonical payment failure a `paymentError()` carries
  // after `classifyPaymentFailure`. Without reading it here, a designed payment
  // rejection (the price moved, the guest is not eligible) looked like an
  // unknown error and was captured to Sentry as a defect (issue #167).
  for (const value of [
    item.error_code,
    item.details?.upstreamCode,
    item.details?.upstream_code,
    item.code,
    item.paymentCode,
  ]) {
    if (typeof value === 'string' && value) return value
  }
  return null
}

function errorStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null
  const e = err as {
    statusCode?: unknown
    status?: unknown
    context?: { status?: unknown }
  }
  for (const value of [e.statusCode, e.status, e.context?.status]) {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value)
  }
  return null
}

function nativeNumericCode(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null
  const code = (err as { code?: unknown }).code
  if (typeof code === 'number' && Number.isFinite(code)) return code
  if (typeof code === 'string' && /^\d+$/.test(code)) return Number(code)
  return null
}

export function isGoogleAuthRecoverableError(err: unknown): boolean {
  const code = errorCode(err)
  if (
    code === 'GOOGLE_SERVER_ERROR' ||
    code === 'GOOGLE_PLAY_SERVICES_UNAVAILABLE' ||
    code === 'GOOGLE_NETWORK_ERROR'
  ) {
    return true
  }
  const haystack = collectErrorText(err).join('\n')
  const nativeCode = nativeNumericCode(err)
  // Google Sign-In CommonStatusCodes.INTERNAL_ERROR is 8. The JS wrapper often
  // arrives as `{ code: 8, message: 'INTERNAL_ERROR' }` with no class name, so
  // a class-name regex never sees TAGO-MOBILE-2C.
  if (nativeCode === 8) {
    const message =
      err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
        ? (err as { message: string }).message
        : ''
    if (
      !message.trim() ||
      /internal_error|gms|play.?services|google/i.test(`${message}\n${haystack}`)
    ) {
      return true
    }
  }
  return (
    /GOOGLE_PLAY_SERVICES_UNAVAILABLE/i.test(haystack) ||
    /GOOGLE_NATIVE_AUTH_FAILED/i.test(haystack) ||
    /GOOGLE_SERVER_ERROR/i.test(haystack) ||
    /Native Google sign-in failed/i.test(haystack) ||
    /com\.google\.android\.gms\.common\.api\.ApiException:\s*INTERNAL_ERROR/i.test(haystack) ||
    /\bApiException:\s*INTERNAL_ERROR\b/i.test(haystack) ||
    // DEVELOPER_ERROR = el cliente OAuth no coincide con el SHA-1/paquete de
    // esta compilación (TAGO-MOBILE-2B). No hay nada que la app pueda corregir
    // en runtime: la persona entra por otra puerta y la configuración del build
    // la verifica la checklist de release, no Sentry.
    /ApiException:\s*DEVELOPER_ERROR/i.test(haystack) ||
    /^(?:com\.google\.android\.gms\.common\.api\.ApiException:\s*)?DEVELOPER_ERROR$/im.test(
      haystack.trim(),
    ) ||
    /^(?:com\.google\.android\.gms\.common\.api\.ApiException:\s*)?INTERNAL_ERROR$/im.test(
      haystack.trim(),
    )
  )
}

/**
 * Google sign-in hit an email that already belongs to another account
 * (email/password or phone). The definitive recovery is to send the user to
 * log in with their existing method — see the auth screens' Google handlers.
 */
/**
 * El servidor pide probar el numero ACTUAL antes de cambiarlo.
 *
 * No es un codigo equivocado: es que falto el paso 1, o que la prueba vencio.
 * Tratarlo como "codigo invalido" deja a la persona reintentando un codigo que
 * nunca va a servir.
 */
export function isPhoneChangeNeedsCurrentError(err: unknown): boolean {
  return /PHONE_CHANGE_NEEDS_CURRENT/i.test(collectErrorText(err).join(String.fromCharCode(10)))
}

export function isGoogleEmailExistsError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /GOOGLE_EMAIL_ALREADY_REGISTERED/i.test(haystack) || /email_already_registered/i.test(haystack)
  )
}

export function isPushNotificationSetupError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /Default FirebaseApp is not initialized/i.test(haystack) ||
    /complete the guide .*fcm-credentials/i.test(haystack) ||
    /getExpoPushTokenAsync/i.test(haystack) ||
    /FCM credentials/i.test(haystack)
  )
}

export function isInvalidCredentialsError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return /auth\.errors\.invalid_credentials|AUTH_INVALID_CREDENTIALS|invalid login credentials|invalid_credentials/i.test(
    haystack,
  )
}

export function isInvalidRefreshTokenError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return /invalid refresh token|refresh token not found|refresh token.*invalid/i.test(haystack)
}

export function isDuplicateAccountRegistrationError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /user already registered/i.test(haystack) ||
    /user_already_exists/i.test(haystack) ||
    /email.*already.*registered/i.test(haystack) ||
    /already.*registered.*email/i.test(haystack)
  )
}

export function isAuthSessionMissingError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /AuthSessionMissingError/i.test(haystack) ||
    /auth session missing/i.test(haystack) ||
    /PASSWORD_RECOVERY_SESSION_MISSING/i.test(haystack) ||
    /PASSWORD_RECOVERY_LINK_MISSING_TOKEN/i.test(haystack)
  )
}

export function isPlayStoreAppNotOwnedError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /ERROR_APP_NOT_OWNED/i.test(haystack) ||
    /Install Error\(-10\)/i.test(haystack) ||
    /Failed to check for updates:\s*-10/i.test(haystack) ||
    /app.*not owned/i.test(haystack) ||
    /Install Error\(-6\)/i.test(haystack) ||
    /Failed to check for updates:\s*-6/i.test(haystack) ||
    /download\/install is not allowed/i.test(haystack) ||
    /current device state/i.test(haystack)
  )
}

export function isReservationDateConflictError(err: unknown): boolean {
  const item = err as { code?: unknown }
  const haystack = collectErrorText(err).join('\n')
  return (
    item?.code === 'DATES_UNAVAILABLE' ||
    (item?.code === 'P0001' && /vehicle already has a reservation in these dates/i.test(haystack))
  )
}

export function isRentalStartInThePastError(err: unknown): boolean {
  const item = err as { code?: unknown }
  const haystack = collectErrorText(err).join('\n')
  return (
    item?.code === 'RENTAL_START_IN_THE_PAST' ||
    /rental start time cannot be in the past/i.test(haystack)
  )
}

/**
 * Host papers expired — DB trigger blocks the insert. Expected product rule,
 * not a crash (TAGO-MOBILE-4A).
 */
export function isVehicleDocumentExpiredBookingError(err: unknown): boolean {
  const item = err as { code?: unknown; hint?: unknown }
  const hint = typeof item?.hint === 'string' ? item.hint : ''
  if (item?.code === 'VEHICLE_DOCUMENT_EXPIRED') return true
  if (/^vehicle_document_expired:/i.test(hint)) return true
  return /documento vencido y no se puede reservar/i.test(collectErrorText(err).join('\n'))
}

/**
 * La póliza del vehículo excluye renta o uso comercial.
 *
 * Es una decisión de producto del verificador de documentos (verify-vehicle-
 * document devuelve la razón y la app la muestra tal cual), no un defecto: el
 * anfitrión subió una póliza que no cubre alquiler. TAGO-MOBILE-58.
 */
export function isVehiclePolicyExclusionError(err: unknown): boolean {
  return /p[óo]liza excluye renta, uso comercial o transporte remunerado/i.test(
    collectErrorText(err).join('\n'),
  )
}

export function isAbortError(err: unknown): boolean {
  const item = err as { name?: unknown; code?: unknown }
  const parts = collectErrorText(err)
  const haystack = parts.join('\n')
  return (
    item?.name === 'AbortError' ||
    item?.code === 'ABORT_ERR' ||
    parts.some((part) => /^abort(?:ed)?$/i.test(part.trim())) ||
    /AbortError/i.test(haystack) ||
    /"(?:value|message)":"abort(?:ed)?"/i.test(haystack) ||
    /The operation was aborted/i.test(haystack) ||
    /signal is aborted without reason/i.test(haystack) ||
    /AuthRetryableFetchError: Aborted/i.test(haystack) ||
    /StorageUnknownError: Aborted/i.test(haystack) ||
    // TAGO-MOBILE-4A: react-native's fetch surfaces an AbortController cancel as
    // `FetchError: Fetch request has been canceled`, not name 'AbortError'. The
    // user released the request before it resolved; that is not a service defect.
    /Fetch request has been canceled/i.test(haystack)
  )
}

export function isOfflineNetworkError(err: unknown): boolean {
  const item = err as { name?: unknown; code?: unknown }
  if (item?.name === 'AuthRetryableFetchError' && !isAbortError(err)) return true
  if (item?.code === 'AUTH_NETWORK_ERROR') return true
  const haystack = collectErrorText(err).join('\n')
  return (
    /Network request failed/i.test(haystack) ||
    /UnknownHostException/i.test(haystack) ||
    /Unable to resolve host/i.test(haystack) ||
    /Failed to send a request to the Edge Function/i.test(haystack) ||
    /TypeError: Failed to fetch/i.test(haystack) ||
    /fetch failed/i.test(haystack)
  )
}

/**
 * Rechazo de una operación OTA de expo-updates (check o fetch).
 *
 * TAGO-MOBILE-5C / 5N / 4M: un update es best-effort y el hook reintenta solo;
 * el único fallo accionable es el que impide APLICAR una update publicada. Por
 * eso acá solo se conservan firma, code signing, manifiesto e inicialización:
 * todo lo demás (red, timeout, `undefined reason` del puente iOS, fetch
 * cortado) es operacional y va como breadcrumb.
 *
 * Se reconoce por el texto, no por `instanceof`: expo-modules-core no expone la
 * clase `CodedError` de forma estable para importarla en cada superficie.
 */
export function isOtaUpdateOperationError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  const isOperation =
    /ERR_UPDATES_(?:CHECK|FETCH)/i.test(haystack) ||
    /checkForUpdateAsync|fetchUpdateAsync/i.test(haystack) ||
    /failed to check for updates?/i.test(haystack)
  if (!isOperation) return false
  if (
    /UpdateHasInvalidSignature|UpdateCodeSigningError|InitializationError|JSRuntimeError/i.test(
      haystack,
    ) ||
    /invalid signature|code signing|manifest/i.test(haystack)
  ) {
    return false
  }
  return true
}

/**
 * El mapa no pudo cargar su estilo o sus tiles.
 *
 * En la gran mayoría de los casos es una pérdida de red ("La conexión de red se
 * perdió"): el mapa ya falla en cerrado y muestra "Mapa no disponible", así que
 * el listado y los filtros siguen usables. Reportarlo como error convierte cada
 * bache de señal en una alerta. Una regresión real de estilo (token o URL rota)
 * afecta a todos a la vez y se detecta por el volumen, no por un evento suelto;
 * el rastro queda igual en los breadcrumbs.
 */
export function isMapTilesLoadError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n').toLowerCase()
  return (
    haystack.includes('failed to load its style or tiles') ||
    haystack.includes('mapload error') ||
    haystack.includes('failed to load tile')
  )
}

export function isBannedUserError(err: unknown): boolean {
  // TAGO-MOBILE-3K / 3J: GoTrue returns `user_banned`; auth.service maps that
  // to ACCOUNT_RESTRICTED + suspended copy. Either shape must stay off Sentry
  // (breadcrumb only) while the toast still fires — expected restriction, not a crash.
  const code = errorCode(err)
  if (code === 'user_banned' || code === 'ACCOUNT_RESTRICTED') return true
  const haystack = collectErrorText(err).join('\n')
  return /user[_ ]is[_ ]banned/i.test(haystack) || /\buser_banned\b/i.test(haystack)
}

/** Host photo upsert denied after session recovery — toast, do not page. */
export function isVehicleImageForbiddenError(err: unknown): boolean {
  return errorCode(err) === 'VEHICLE_IMAGE_FORBIDDEN'
}

export function isExpiredAuthLinkError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return /Email link is invalid or has expired/i.test(haystack)
}

export function isAppleAuthCancelError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  // RequestUnknownException is Apple/OS failing, not a user dismiss.
  // TAGO-MOBILE-3H was misclassified as cancel and still paged because the
  // ServiceError message hid the original text.
  if (
    /RequestUnknownException/i.test(haystack) ||
    /ERR_REQUEST_UNKNOWN/i.test(haystack) ||
    /authorization attempt failed for an unknown reason/i.test(haystack)
  ) {
    return false
  }
  const code = errorCode(err)
  return (
    code === 'ERR_CANCELED' ||
    code === 'ERR_REQUEST_CANCELED' ||
    code === '1001' ||
    code === 'APPLE_AUTH_CANCELED' ||
    /ERR_REQUEST_CANCELED|ERR_CANCELED/i.test(haystack) ||
    /\bcancel(?:led|ed)?\b/i.test(haystack)
  )
}

/** Simulator / device without Apple ID — expected, user can use another door. */
export function isAppleAuthUnavailableError(err: unknown): boolean {
  return errorCode(err) === 'APPLE_AUTH_UNAVAILABLE'
}

function isEdgeFunctionHttpError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const item = err as { name?: unknown; code?: unknown }
  const haystack = collectErrorText(err).join('\n')
  return (
    item.name === 'FunctionsHttpError' ||
    item.name === 'VerificationHttpError' ||
    item.code === 'FUNCTIONS_HTTP_ERROR' ||
    item.code === 'VERIFICATION_HTTP_ERROR' ||
    /Edge Function returned a non-2xx/i.test(haystack)
  )
}

// Transport status alone never proves a designed product outcome. Unknown
// authorization, validation and schema failures must remain observable.
const EXPECTED_EDGE_CLIENT_OUTCOME_CODES = new Set([
  'AUTH_INVALID_CREDENTIALS',
  'AUTH_SESSION_EXPIRED',
  'AUTH_SESSION_MISSING',
  'DATES_UNAVAILABLE',
  'OTP_INVALID',
  'OTP_USE_EMAIL_LOGIN',
  'PHONE_ACCOUNT_NOT_FOUND',
  'PHONE_IN_USE',
  'PHONE_INVALID',
  'PHONE_UNDELIVERABLE',
  'PHONE_USER_NOT_FOUND',
  'RENTAL_START_IN_THE_PAST',
  'VALIDATION_ERROR',
  'VEHICLE_DOCUMENT_EXPIRED',
])
const EXPECTED_EDGE_AUTH_OUTCOME_CODES = new Set(['AUTH_SESSION_EXPIRED', 'AUTH_SESSION_MISSING'])

export function isUnauthorizedEdgeFunctionError(err: unknown): boolean {
  const status = errorStatus(err)
  return (
    isEdgeFunctionHttpError(err) &&
    (status === 401 || status === 403) &&
    EXPECTED_EDGE_AUTH_OUTCOME_CODES.has(errorCode(err) ?? '')
  )
}

/** Only explicit service outcomes, never every Edge 4xx, are operational. */
export function isExpectedClientHttpError(err: unknown): boolean {
  if (!isEdgeFunctionHttpError(err)) return false
  const status = errorStatus(err)
  if (status !== null && (status < 400 || status >= 500)) return false
  return EXPECTED_EDGE_CLIENT_OUTCOME_CODES.has(errorCode(err) ?? '')
}

/**
 * El escáner de documentos se quedó sin tiempo (TAGO-MOBILE-3A).
 *
 * Lo dicta la cámara del sistema y el entorno del teléfono, no una ruta nuestra:
 * la persona reintenta y sigue. Reportarlo como error convierte cada scan lento
 * en una alerta.
 */
/**
 * App Hangs atrapados dentro de frameworks del sistema.
 *
 * Sentry marca "App Hanging ≥2000 ms" cuando el hilo principal se traba. Dos
 * culpables vistos en producción NO están en nuestro código: VisionKit calcula
 * la nitidez de la foto del documento (vDSP) en el hilo principal al capturar,
 * y el teclado de iOS espera al autofill en el mismo hilo. En un iPhone con
 * cámara grande esos dos pasan de dos segundos solos. Se quedan fuera del canal
 * de "App Hanging" para que ese canal siga significando algo cuando el culpable
 * sí sea la app (TAGO-MOBILE-4F).
 */
const SYSTEM_FRAMEWORK_HANG_FRAMES = [
  'ICDocCam',
  'sharpnessForImageBuffer',
  'vDSP_',
  'vConvolve',
  'conv4F_',
  'AVPerformDelegateCallback',
  '_UIKeyboardStateManager',
  'UIKeyboardTaskQueue',
  'generateAutofillCandidate',
  // CoreGraphics reduciendo imágenes (EXIF/miniatura) en el hilo principal en
  // un iPhone viejo: TAGO-MOBILE-5B (ripc_DrawImage → resample_horizontal).
  'ripc_',
  'CGSImageDataLock',
  'img_data_lock',
  'img_interpolate',
  'resample_',
] as const

export function isSystemFrameworkAppHang(err: unknown, stackText?: string): boolean {
  const combined = `${collectErrorText(err).join('\n')}\n${stackText ?? ''}`
  if (!/App Hanging|App hanging for at least/i.test(combined)) return false
  return SYSTEM_FRAMEWORK_HANG_FRAMES.some((frame) => combined.includes(frame))
}

export function isDocumentScanTimeoutError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    errorCode(err) === 'DOCUMENT_SCAN_TIMEOUT' ||
    /DocumentScanTimeoutError/i.test(haystack) ||
    /document scanner timed out/i.test(haystack)
  )
}

/**
 * El envío del anuncio se pasó del presupuesto subiendo fotos (TAGO-MOBILE-4S).
 *
 * El mensaje ya le dice a la persona que revise la conexión y reintente; el
 * presupuesto existe para no dejar la pantalla colgada para siempre. Es una red
 * lenta, no un defecto del producto.
 */
export function isListingSubmitTimeoutError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    errorCode(err) === 'LISTING_SUBMIT_TIMEOUT' ||
    /ListingSubmitTimeoutError/i.test(haystack) ||
    /No se pudo terminar de subir las fotos a tiempo/i.test(haystack)
  )
}

/**
 * Dispositivo o emulador sin OpenGL ES 3.0 (TAGO-MOBILE-4J).
 *
 * Native capability failures still need a supported-device/native fix.
 * This helper identifies the cause; it must never suppress the fatal event.
 */
export function isUnsupportedGraphicsError(err: unknown): boolean {
  return /OpenGL ES 3\.0 context could not be created/i.test(collectErrorText(err).join('\n'))
}

/** Sondeo de un escáner externo contra el DSN público: no es un error del producto. */
export function isSecurityScanProbeError(err: unknown): boolean {
  return /security_scan_validation_probe/i.test(collectErrorText(err).join('\n'))
}

/**
 * Carrera conocida del FragmentManager de Android dentro de react-native-screens
 * (`ScreenContainer.onDetachedFromWindow` → `executePendingTransactions`).
 * A native fix/release is needed; this helper does not make it operational
 * noise. Keep the event observable so regressions can reopen the issue.
 */
export function isAndroidFragmentRaceError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /Cannot remove child at index \d+ from parent ViewGroup/i.test(haystack) ||
    /FragmentManager is already executing transactions/i.test(haystack)
  )
}

/** Map/WebView native handle already gone after a screen unmount. */
export function isStaleNativeViewError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /view:\s*\d+\s+but is null \(timeout or resolve\)/i.test(haystack) ||
    // TAGO-MOBILE-47: Mapbox polls setHandledMapChangedEvents after JS unmounted.
    /Could not find view with tag\s+\d+\s+in\s+\S+\s+after\s+\d+\s+attempts over\s+[\d.]+ms/i.test(
      haystack,
    )
  )
}

export function isTransientClientTimeoutError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return (
    /App version policy request timed out/i.test(haystack) ||
    // TAGO-MOBILE-5P: el endpoint del muro devolvió un 5xx (p. ej. 504 del edge).
    // El gate ya falla-abierto con la política cacheada; reintenta en el próximo
    // focus. Es infraestructura transitoria, no un defecto de la app.
    /App version policy failed with 5\d\d/i.test(haystack) ||
    /returned 429/i.test(haystack) ||
    /Edge function .* returned 429/i.test(haystack)
  )
}

/**
 * Una respuesta 200 ILEGIBLE del transporte de verificación (un proxy o
 * gateway devolvió algo que no es JSON) es infraestructura transitoria, no un
 * defecto de la app: el flujo ya reconcilia por read-back y la mutación es
 * facturable — por eso el transporte no reintenta solo. TAGO-MOBILE-5S.
 */
export function isUnreadableVerificationResponseError(err: unknown): boolean {
  return /Verification response was unreadable/i.test(collectErrorText(err).join('\n'))
}

/** Wrong OTP typed by the person. Expected, never a crash. */
export function isInvalidOtpError(err: unknown): boolean {
  const code = errorCode(err)
  if (code === 'OTP_INVALID') return true
  const haystack = collectErrorText(err).join('\n')
  return /That code is not valid/i.test(haystack)
}

export function isPhoneTakenError(err: unknown): boolean {
  return (
    isPhoneInUseError(err) ||
    /That number already belongs to another account/i.test(collectErrorText(err).join('\n'))
  )
}

export function isExpectedDocumentMismatchError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return /no coincide con el veh[ií]culo/i.test(haystack)
}

export function isValidationServiceError(err: unknown): boolean {
  return errorCode(err) === 'VALIDATION_ERROR'
}

/**
 * Clasifica el fallo HTTP de una verificación (TAGO-MOBILE-57).
 *
 * Un 4xx —salvo 408— es el servidor rechazando ESTA entrada ("Revisá los datos
 * del documento antes de confirmar"): un resultado esperado que se le muestra a
 * la persona y NO se reporta a Sentry. Forzarlo a un 500 genérico
 * (`VERIFICATION_FAILED`) convertía un error de tipeo en una alerta de
 * producción y enterraba las fallas reales entre el ruido. 5xx y 408 sí son
 * defectos y conservan su evidencia.
 */
export function verificationFailureClass(httpStatus: number | null | undefined): {
  code: 'VALIDATION_ERROR' | 'VERIFICATION_FAILED'
  statusCode: number
} {
  const status =
    typeof httpStatus === 'number' && Number.isFinite(httpStatus) && httpStatus > 0
      ? httpStatus
      : 500
  if (status >= 400 && status < 500 && status !== 408) {
    return { code: 'VALIDATION_ERROR', statusCode: status }
  }
  return { code: 'VERIFICATION_FAILED', statusCode: status }
}

/**
 * The photo batch is a user-visible, retryable result, not a second root
 * failure. `InspectionService` already captures each failed area with the
 * storage/rules error that caused it. Capturing this aggregate as another
 * exception created TAGO-MOBILE-4Q beside TAGO-MOBILE-4P for the same attempt.
 * Keep the aggregate as a breadcrumb while the per-photo cause remains the
 * Sentry event engineers can actually fix.
 */
export function isInspectionPhotosBatchError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const item = err as { code?: unknown; details?: { failedAreas?: unknown } }
  return (
    item.code === 'INSPECTION_PHOTOS_FAILED' &&
    Array.isArray(item.details?.failedAreas) &&
    item.details.failedAreas.length > 0
  )
}

export function isStaleWebViewError(err: unknown): boolean {
  const haystack = collectErrorText(err).join('\n')
  return /Java object is gone/i.test(haystack) || /Error invoking postMessage/i.test(haystack)
}

/**
 * Designed payment rejections — product answers, not defects.
 *
 * `create-payment` answers 4xx with a machine code for outcomes the product
 * already models: the hold died, the price moved, the guest is not eligible,
 * the 3DS window closed. The checkout shows each one its own copy; none of
 * them is a broken thing to investigate. Capturing them sent business answers
 * to Sentry as if they were outages (issues #166 and #167 of the error loop).
 *
 * What is deliberately absent: `APP_NOT_LIVE`, `CREATE_FAILED`,
 * `HOSTED_CHECKOUT_FAILED` and `UNKNOWN`. Those mean the charge path itself
 * failed — the merchant could not collect, or nobody can say why — and they
 * must keep reaching Sentry.
 */
const DESIGNED_PAYMENT_REJECTION_CODES = new Set([
  'COMPLIANCE_INCOMPLETE',
  'RENTAL_NO_LONGER_ACTIVE',
  'RENTAL_TOTAL_MISMATCH',
  'SAVED_CARD_DISABLED',
  'CHALLENGE_ABANDONED',
  'START_IN_PAST',
  'HOSTED_CHECKOUT_DISABLED',
  'BOOKING_TOO_SOON',
  'HOST_DECISION_UNAVAILABLE',
])

export function isDesignedPaymentRejection(err: unknown): boolean {
  const code = errorCode(err)
  return code !== null && DESIGNED_PAYMENT_REJECTION_CODES.has(code)
}

export function isExpectedNonFatalOperationalError(err: unknown, level?: string): boolean {
  if (level === 'fatal' || isAndroidFragmentRaceError(err) || isUnsupportedGraphicsError(err)) {
    return false
  }
  const googleText = collectErrorText(err).join('\n')
  if (
    nativeNumericCode(err) === 8 ||
    nativeNumericCode(err) === 10 ||
    /DEVELOPER_ERROR|INTERNAL_ERROR|GOOGLE_INTERNAL_ERROR|GOOGLE_AUTH_NOT_CONFIGURED|GOOGLE_ACCESS_BLOCKED|GOOGLE_NATIVE_AUTH_FAILED|GOOGLE_NATIVE_ID_TOKEN_MISSING|GOOGLE_SERVER_ERROR/i.test(
      googleText,
    )
  ) {
    // Keep UX retry/fallback helpers, but configuration and provider/internal
    // failures are defects even when the person can use another login method.
    return false
  }
  return (
    errorCode(err) === 'same_password' ||
    errorCode(err) === 'weak_password' ||
    errorCode(err) === 'BOOKING_TOO_SOON' ||
    errorCode(err) === 'HOST_DECISION_UNAVAILABLE' ||
    isDesignedPaymentRejection(err) ||
    // Only the CAPACITY half of OTP delivery is expected. This used to read
    // `isPhoneOtpDeliveryError`, which also swallowed `provider_rejected` into
    // a breadcrumb — so when Twilio stopped delivering, Sentry stayed silent
    // and the failure looked, from every dashboard we own, like users failing
    // to type a code. A provider rejection is an outage and falls through to
    // captureException on purpose.
    isPhoneOtpRateLimitError(err) ||
    // A number that cannot receive SMS is user input, not a crash.
    isInvalidPhoneNumberError(err) ||
    isGoogleAuthRecoverableError(err) ||
    isPushNotificationSetupError(err) ||
    isInvalidCredentialsError(err) ||
    isInvalidRefreshTokenError(err) ||
    isDuplicateAccountRegistrationError(err) ||
    isAuthSessionMissingError(err) ||
    isPlayStoreAppNotOwnedError(err) ||
    isReservationDateConflictError(err) ||
    isRentalStartInThePastError(err) ||
    isVehicleDocumentExpiredBookingError(err) ||
    isVehiclePolicyExclusionError(err) ||
    isAbortError(err) ||
    isOfflineNetworkError(err) ||
    // Generic Expo rejection is not evidence of a network failure; only an
    // operation that cannot apply a published update is actionable (5C/5N/4M).
    isOtaUpdateOperationError(err) ||
    isMapTilesLoadError(err) ||
    isBannedUserError(err) ||
    isVehicleImageForbiddenError(err) ||
    isExpiredAuthLinkError(err) ||
    isAppleAuthCancelError(err) ||
    isAppleAuthUnavailableError(err) ||
    isUnauthorizedEdgeFunctionError(err) ||
    isExpectedClientHttpError(err) ||
    isTransientClientTimeoutError(err) ||
    isUnreadableVerificationResponseError(err) ||
    isPhoneTakenError(err) ||
    isPhoneInUseError(err) ||
    isInvalidOtpError(err) ||
    isExpectedDocumentMismatchError(err) ||
    isValidationServiceError(err) ||
    isInspectionPhotosBatchError(err) ||
    isStaleWebViewError(err) ||
    isStaleNativeViewError(err) ||
    isDocumentScanTimeoutError(err) ||
    isListingSubmitTimeoutError(err) ||
    isSecurityScanProbeError(err)
  )
}

function collectErrorText(err: unknown, seen = new Set<unknown>()): string[] {
  if (!err || seen.has(err)) return []
  seen.add(err)

  if (typeof err === 'string') return [err]
  if (typeof err !== 'object') return [String(err)]

  /*
   * `msg`, `error_code` y `error_description` no son adornos: son los nombres
   * que usa el CUERPO JSON de GoTrue. Un AuthApiError de supabase-js trae el
   * texto en `message`, pero cualquier ruta que devuelva la respuesta cruda lo
   * trae en `msg` — y `error_code` es el único campo pensado para que lo lea una
   * máquina. Sin ellos, `{"error_code":"sms_send_failed","msg":"…"}` era un
   * objeto del que esta función no extraía ni una palabra, así que todo
   * clasificador que dependa de ella lo veía como un error desconocido.
   */
  const item = err as {
    name?: unknown
    message?: unknown
    msg?: unknown
    code?: unknown
    error_code?: unknown
    error_description?: unknown
    status?: unknown
    cause?: unknown
  }
  return [
    item.name,
    item.message,
    item.msg,
    item.code,
    item.error_code,
    item.error_description,
    item.status,
    ...collectErrorText(item.cause, seen),
  ]
    .filter(
      (value): value is string | number => typeof value === 'string' || typeof value === 'number',
    )
    .map(String)
}

/**
 * Los trámites que le faltan a quien intentó reservar, según el servidor.
 *
 * `private.assert_rental_insert_is_a_request` los manda en el HINT porque un
 * HINT viaja hasta el cliente por PostgREST y un mensaje de excepción no sirve:
 * `errorMessage()` nunca muestra `err.message` en producción, a propósito, para
 * que un SQLSTATE jamás llegue a un toast. Así que el texto es para el ingeniero
 * y el HINT es para la máquina.
 *
 * Devuelve `null` cuando el HINT no es de este trigger, para que el resto del
 * mapeo siga su curso normal.
 */
const RENTAL_ELIGIBILITY_HINT = 'rental_insert_requires_eligibility:'
const RENTAL_PHONE_HINT = 'rental_insert_requires_verified_phone'
const RENTAL_VEHICLE_NOT_RESERVABLE_HINT = 'rental_insert_vehicle_not_reservable'

function rentalEligibilityBlockersFrom(hint: string | null | undefined): string[] | null {
  if (typeof hint !== 'string') return null

  if (hint === RENTAL_PHONE_HINT) return ['phone']

  if (!hint.startsWith(RENTAL_ELIGIBILITY_HINT)) return null

  const blockers = hint
    .slice(RENTAL_ELIGIBILITY_HINT.length)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)

  // Un HINT del trigger sin ni un bloqueador es una contradicción: el servidor
  // dijo que no se puede pero no dijo por qué. Se reconoce igual --el rechazo es
  // real-- y la pantalla cae en su texto de "no pudimos determinar qué falta",
  // que es lo honesto.
  return blockers
}
